import { Component, OnInit, Output, EventEmitter, Optional } from '@angular/core';
import { FlightWatchHistorical } from 'src/app/models/flight-watch-historical';

import { SharedService } from '../../../layouts/shared-service';
import { CustomersListType } from '../../../models/customer';
// Services
import { AircraftsService } from '../../../services/aircrafts.service';
import { CustomeraircraftsService } from '../../../services/customeraircrafts.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';

export interface NewCustomerAircraftDialogData {
    tailNumber: string;
    customers: CustomersListType[];
    make: string;
    model: string;
}

export interface AircraftAssignResult extends Partial<FlightWatchHistorical> {
    customerAircraftId: number;
}

@Component({
    providers: [SharedService],
    selector: 'app-aircraft-assign-modal',
    styleUrls: ['./aircraft-assign-modal.component.scss'],
    templateUrl: './aircraft-assign-modal.component.html',
})
export class AircraftAssignModalComponent implements OnInit {
    // Public Members
    public aircraftSizes: Array<any>;
    public aircraftTypes: Array<any>;
    public selectedAircraft: any;
    public loading: boolean;
    public selectedCustomer: CustomersListType | null = null;
    public isCustomerEditable: boolean = false;
    public customerFilter: string = '';
    public chartName = 'aicraft-assign';
    public isLoadingData = true;

    // Data property - will be set by dialog wrapper
    private _data: NewCustomerAircraftDialogData;
    get data(): NewCustomerAircraftDialogData {
        return this._data;
    }
    set data(value: NewCustomerAircraftDialogData) {
        this._data = value;
        if (value && value.customers && value.customers.length > 0) {
            this.selectedCustomer = value.customers[0];
        }
    }

    // EventEmitters for dialog communication
    @Output() aircraftAdded = new EventEmitter<AircraftAssignResult>();
    @Output() cancelDialog = new EventEmitter<void>();

    constructor(
        @Optional() private aircraftsService: AircraftsService,
        @Optional() private customerAircraftsService: CustomeraircraftsService,
        @Optional() private sharedService: SharedService,
        @Optional() private ngxLoader: NgxUiLoaderService,
    ) {}

    ngOnInit() {
        if (this.ngxLoader) {
            this.ngxLoader.startLoader(this.chartName);
        }
        if (this.aircraftsService) {
            this.aircraftsService
                .getAll()
                .subscribe((reponse: any) => {
                    this.aircraftTypes = reponse;
                    if (this.data?.make && this.data?.model) {
                        this.selectedAircraft = this.aircraftTypes.find(
                            a =>
                                a.make.trim().toUpperCase() == this.data.make.trim().toUpperCase() &&
                                a.model.trim().toUpperCase() == this.data.model.trim().toUpperCase()
                        );
                    }
                    this.closeLoaderSpinner();
                });
            this.aircraftsService
                .getAircraftSizes()
                .subscribe((data: any) => {
                    this.aircraftSizes = data;
                    this.closeLoaderSpinner();
                });
        }
    }
    private closeLoaderSpinner(): void {
        if(this.isLoadingData){
            this.isLoadingData = false;
        }else{
            if (this.ngxLoader) {
                this.ngxLoader.stopLoader(this.chartName);
            }
        }
    }
    public onClose() {
        this.cancelDialog.emit();
    }

    public onAircraftSelected(aircraft: any) {
        this.selectedAircraft = aircraft;
    }

    public onCustomerSelected(customer: CustomersListType) {
        this.selectedCustomer = customer;
        this.isCustomerEditable = false;
    }

    public onCustomerFilterChanged(filter: string) {
        this.customerFilter = filter;
        // If user types something that doesn't match, enable custom customer mode
        if (filter && !this.data?.customers?.some(c => c.company.toLowerCase() === filter.toLowerCase())) {
            this.isCustomerEditable = true;
        } else {
            this.isCustomerEditable = false;
        }
    }

    addCustomCustomer(){
        this.isCustomerEditable = !this.isCustomerEditable;
        if (this.isCustomerEditable) {
            this.selectedCustomer = null;
            this.customerFilter = '';
        }
    }
    public onSubmit() {
        this.loading = true;
        const payload: any = {
            aircraftId: this.selectedAircraft.aircraftId,
            fboId: this.sharedService?.currentUser?.fboId,
            groupId: this.sharedService?.currentUser?.groupId,
            size: this.selectedAircraft.size,
            tailNumber: this.data?.tailNumber,
            isFavoriteAircraft : false
        };

        // Check if custom customer (string) or selected customer (object)
        if (this.isCustomerEditable && this.customerFilter) {
            // Custom customer - create new customer
            payload.customer = this.customerFilter;
            if (this.customerAircraftsService) {
                this.customerAircraftsService
                    .createAircraftWithCustomer(payload)
                    .subscribe((result: any) => {
                        if (this.data?.customers) {
                            this.data.customers.push({
                                customerInfoByGroupID : result.customerInfoByGroup.oid,
                                companyId : result.customerInfoByGroup.customerId,
                                company : result.customerInfoByGroup.company
                            });
                        }

                        this.aircraftAdded.emit({
                            aircraftType:
                                this.selectedAircraft.make +
                                ' / ' +
                                this.selectedAircraft.model,
                            company: result.customerInfoByGroup.company,
                            companyId: result.customerInfoByGroup.customerId,
                            customerInfoByGroupID: result.customerInfoByGroup.oid,
                            customerAircraftId: result.customerAircraft.oid,
                        } as AircraftAssignResult);
                    });
            }
        } else if (this.selectedCustomer) {
            // Selected existing customer
            payload.customerId = this.selectedCustomer.companyId;
            if (this.customerAircraftsService) {
                this.customerAircraftsService.add(payload, this.sharedService?.currentUser?.oid, this.sharedService?.currentUser?.fboId).subscribe((result: any) => {
                    this.aircraftAdded.emit({
                        aircraftType:
                            this.selectedAircraft.make +
                            ' / ' +
                            this.selectedAircraft.model,
                        company: this.selectedCustomer.company,
                        companyId: this.selectedCustomer.companyId,
                        customerInfoByGroupID: this.selectedCustomer.customerInfoByGroupID,
                        customerAircraftId: result.oid,
                    } as AircraftAssignResult);
                });
            }
        }
    }
    isSubmitDisabled(): boolean {
        const hasCustomer = this.isCustomerEditable 
            ? this.customerFilter && this.customerFilter.trim() !== ''
            : this.selectedCustomer !== null;
        
        return this.loading || 
               !this.data?.tailNumber || 
               !this.selectedAircraft || 
               !hasCustomer;
    }
    public displayAircraft(aircraft) {
        return aircraft ? `${aircraft.make}/${aircraft.model}` : aircraft;
    }

    public displayCustomer(customer: CustomersListType) {
        return customer ? customer.company : customer;
    }
}
